
import { ROOT_ACTIONS } from "@/store/actions";
import { Component, Vue } from "vue-property-decorator";
import { auth, firestore, storage } from '@/firebase';
import { browserLocalPersistence, signInWithEmailLink, isSignInWithEmailLink, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { getDoc, doc, query, collection, where, getDocs } from "firebase/firestore";
import { LOGIN_BLOCKED_STORAGE_KEY, USER_ACTIONS } from "@/store/user/actions";
import { USER_GETTERS } from "@/store/user/getters";
import { User } from "@/store/user/types";
import axios from 'axios'
import { getDownloadURL, ref } from "firebase/storage";
import { getManualPath } from "@/utils/manuals";

const EMAIL_FOR_SIGN_IN_KEY = 'emailForSignIn';

@Component
export default class Login extends Vue {
  user: null | User = null;

  loginLinkSent = false;
  completeLoginLoading = false;
  needsEmailForLink = false;
  loginMessage = '';

  step = 1;
  inputs = {
    email: "",
    password: "",
  };

  loading = {
    submit: false,
  };

  async mounted() {
    // Set language to browser/system language for login page
    const browserLang = window.navigator.language;
    // Map browser language codes to supported locales
    if (browserLang.startsWith('nl')) {
      this.$i18n.locale = 'nl';
    } else if (browserLang.startsWith('fr')) {
      this.$i18n.locale = 'fr';
    } else {
      this.$i18n.locale = 'nl'; // Default to Dutch
    }

    this.$store.dispatch(ROOT_ACTIONS.SET_BREADCRUMBS, [])
    this.consumeLoginBlockedFlag();

    if (isSignInWithEmailLink(auth, window.location.href)) {
      const storedEmail = window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY)
      if (storedEmail) {
        await this.completeLogin(storedEmail)
      } else {
        // Link opened in another browser/app than where it was requested
        // (e.g. the in-app browser of a mail client): ask for the address in-page.
        this.needsEmailForLink = true
      }
    }
  }

  consumeLoginBlockedFlag(): boolean {
    const reason = window.sessionStorage.getItem(LOGIN_BLOCKED_STORAGE_KEY)
    if (!reason) return false
    window.sessionStorage.removeItem(LOGIN_BLOCKED_STORAGE_KEY)
    this.loginMessage = this.$t('loginAccountArchived').toString()
    return true
  }

  async confirmEmailForLink() {
    const email = this.inputs.email.trim().toLowerCase()
    if (!email) return
    await this.completeLogin(email)
  }

  async completeLogin(email: string) {
    this.completeLoginLoading = true;
    this.loginMessage = '';
    try {
      const authResponse = await signInWithEmailLink(auth, email.trim().toLowerCase(), window.location.href)
      window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY)
      this.needsEmailForLink = false
      await this.finishLogin(authResponse.user.uid)
    } catch (error: any) {
      console.error('Email link sign-in failed', error)
      if (error?.code === 'auth/invalid-email') {
        this.needsEmailForLink = true
        this.loginMessage = this.$t('loginLinkWrongEmail').toString()
      } else {
        this.needsEmailForLink = false
        this.loginMessage = this.loginErrorMessage(error)
        // The link is single-use: drop it from the URL so a refresh shows the login form.
        window.history.replaceState(null, '', '/login')
      }
    } finally {
      this.completeLoginLoading = false;
    }
  }

  async finishLogin(uid: string) {
    const userDoc = await getDoc(doc(firestore, "users", uid));
    if (!userDoc.exists()) {
      await signOut(auth)
      throw new Error('no-profile')
    }
    await this.$store.dispatch(USER_ACTIONS.AUTHENTICATE_USER)
    if (!this.$store.getters[USER_GETTERS.IS_AUTHENTICATED]) {
      if (!this.consumeLoginBlockedFlag()) {
        this.loginMessage = this.$t('loginErrorGeneric').toString()
      }
      this.resetLoginForm()
      return
    }
    location.href = `/${userDoc.data().type.toLowerCase()}`
  }

  loginErrorMessage(error: any): string {
    const code = error?.code || error?.message
    if (code === 'auth/invalid-action-code' || code === 'auth/expired-action-code') {
      return this.$t('loginLinkExpired').toString()
    }
    if (code === 'no-profile') {
      return this.$t('loginErrorNoUser').toString()
    }
    if (code === 'auth/wrong-password' || code === 'auth/invalid-credential' || code === 'auth/invalid-login-credentials') {
      return this.$t('loginWrongPassword').toString()
    }
    return this.$t('loginErrorGeneric').toString()
  }

  resetLoginForm() {
    this.user = null
    this.loginLinkSent = false
    this.inputs.password = ''
  }

  async sendLoginEmail() {
    try {
      this.loading.submit = true;
      this.loginMessage = '';
      const email = this.inputs.email.trim().toLowerCase()
      await axios.post('/functions/sendLoginLink', { data: { email, url: window.location.href } })
      this.loginLinkSent = true;
    } catch (error: any) {
      if (error?.response?.data?.error === 'account_archived') {
        this.loginMessage = this.$t('loginAccountArchived').toString()
        this.resetLoginForm()
      } else {
        this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
          color: "error",
          text: this.$t('loginErrorGeneric'),
        });
      }
    } finally {
      this.loading.submit = false;
    }
  }


  async loginWithPassword() {
    try {
      this.loading.submit = true
      this.loginMessage = ''
      const email = this.inputs.email.trim().toLowerCase()
      const authResponse = await signInWithEmailAndPassword(auth, email, this.inputs.password)
      await this.finishLogin(authResponse.user.uid)
    } catch (error: any) {
      this.loginMessage = this.loginErrorMessage(error)
    } finally {
      this.loading.submit = false
    }
  }

  async login() {
    this.loading.submit = true;
    this.loginMessage = '';
    try {

      const email = this.inputs.email.trim().toLowerCase()
      // set the login persistance
      await auth.setPersistence(browserLocalPersistence)
      window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email)

      const userDocuments = await getDocs(query(collection(firestore, 'users'), where('email', '==', email)))

      if (userDocuments.empty) {
        throw this.$t('loginErrorNoUser');
      }

      this.user = userDocuments.docs[0].data() as User

      if (!this.user.hasPassword) {
        await this.sendLoginEmail()
      }
    } catch (error) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "error",
        text: error,
      });
    } finally {
      this.loading.submit = false;
    }
  }

  async openManual() {
    const url = await getDownloadURL(ref(storage, getManualPath('participant', this.$i18n.locale)));
    window.open(url, '_blank');
  }
}
